/**
 * <predictive-search> — Shopify Predictive Search API ile canlı sonuçlar
 *  - 2+ karakter debounce 200ms
 *  - product (4) + collection (2) + page (2) + article (2)
 *  - Son 10 sorguyu cache'le (Map)
 *  - Aktif istek abort
 *  - Klavye desteği (↑↓ ile sonuçlar arasında gezin, Enter ile tıkla)
 *
 * Header'daki [data-psearch-open] tetiklerken modal açar.
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector('[data-psearch-dialog]');
    this.input = this.querySelector('[data-psearch-input]');
    this.results = this.querySelector('[data-psearch-results]');
    this.closeBtn = this.querySelector('[data-psearch-close]');

    this.cache = new Map();
    this.abortCtrl = null;
    this.debounceId = null;

    this.input?.addEventListener('input', () => this.onInput());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dialog?.open) this.close();
    });

    // Trigger setup (header'daki search ikon)
    document.querySelectorAll('[data-psearch-open]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }

  open() {
    this.dialog?.showModal();
    setTimeout(() => this.input?.focus(), 50);
  }

  close() {
    this.dialog?.close();
    this.input.value = '';
    this.results.innerHTML = '<div class="psearch__hint small">Aramaya başla…</div>';
  }

  onInput() {
    clearTimeout(this.debounceId);
    const q = this.input.value.trim();
    if (q.length < 2) {
      this.results.innerHTML = '<div class="psearch__hint small">En az 2 karakter yaz…</div>';
      return;
    }
    this.debounceId = setTimeout(() => this.search(q), 200);
  }

  async search(q) {
    if (this.cache.has(q)) {
      this.render(this.cache.get(q));
      return;
    }

    if (this.abortCtrl) this.abortCtrl.abort();
    this.abortCtrl = new AbortController();

    const params = new URLSearchParams({
      q,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '4',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor',
    });

    try {
      const res = await fetch(`/search/suggest.json?${params}`, { signal: this.abortCtrl.signal });
      if (!res.ok) throw new Error('search failed');
      const data = await res.json();
      // Cache (en fazla 10 sorgu sakla)
      this.cache.set(q, data);
      if (this.cache.size > 10) this.cache.delete(this.cache.keys().next().value);
      this.render(data);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.results.innerHTML = '<div class="psearch__hint small">Arama hatası.</div>';
    }
  }

  render(data) {
    const r = data.resources?.results || {};
    const products = r.products || [];
    const collections = r.collections || [];
    const pages = r.pages || [];
    const articles = r.articles || [];

    const total = products.length + collections.length + pages.length + articles.length;
    if (total === 0) {
      this.results.innerHTML = `<div class="psearch__empty"><p>Sonuç bulunamadı.</p><p class="small">Popüler: <a href="/collections/elbise">Elbise</a> · <a href="/collections/ceket">Ceket</a> · <a href="/collections/pantolon">Pantolon</a></p></div>`;
      return;
    }

    let html = '';

    if (products.length) {
      html += '<div class="psearch__group"><h3 class="psearch__group__title small">Ürünler</h3><ul class="psearch__products">';
      for (const p of products.slice(0, 4)) {
        const img = p.featured_image
          ? `<img src="${p.featured_image.url}" alt="${p.title}" width="56" height="70" loading="lazy">`
          : '<div class="psearch__noimg"></div>';
        const price = p.price ? `<span class="psearch__price">${p.price}</span>` : '';
        html += `<li><a href="${p.url}" class="psearch__product">
          <span class="psearch__product__media">${img}</span>
          <span class="psearch__product__body">
            <span class="psearch__product__title">${p.title}</span>
            ${price}
          </span>
        </a></li>`;
      }
      html += '</ul></div>';
    }

    if (collections.length) {
      html += '<div class="psearch__group"><h3 class="psearch__group__title small">Kategoriler</h3><ul class="psearch__simple">';
      for (const c of collections.slice(0, 2)) {
        html += `<li><a href="${c.url}">${c.title}</a></li>`;
      }
      html += '</ul></div>';
    }

    if (pages.length || articles.length) {
      html += '<div class="psearch__group"><h3 class="psearch__group__title small">Sayfalar</h3><ul class="psearch__simple">';
      for (const p of pages.slice(0, 2)) html += `<li><a href="${p.url}">${p.title}</a></li>`;
      for (const a of articles.slice(0, 2)) html += `<li><a href="${a.url}">${a.title}</a></li>`;
      html += '</ul></div>';
    }

    this.results.innerHTML = html;
  }
}

customElements.define('predictive-search', PredictiveSearch);
